import { Button } from '@hue/design-system';
import { ArrowLeft, Plus } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import { usePageHelp } from './page-help-context.js';

/// O topo de toda página: trilha, título, frase de para que a página serve, as ações e o ícone de
/// Ajuda. Cada tela montava esse bloco à mão, e cada uma ficava de um jeito: o espaço entre a
/// trilha e o título, o espaço até a lista, onde o botão de ação morava. Aqui o desenho é um só; a
/// tela diz só o texto e quais ações tem.
///
/// - `trail` segue "Grupo / Página", como o menu mostra.
/// - `onBack` põe "Voltar" no começo da trilha, nas telas que abrem por cima de outra.
/// - `aside` acompanha o próprio título (uma dica, a situação do registro) e fica colado nele.
/// - `actions` são os botões da página: à direita no computador, embaixo do texto no celular.
/// - `children` é a linha de detalhes de um registro (telefone, horário), embaixo da descrição.
///
/// O espaço até o conteúdo é sempre o mesmo `mb-6`. A tela não põe o cabeçalho dentro de grade
/// com `gap`, que somaria ao espaço e voltaria a variar.
export function PageHeader({
  trail,
  title,
  id,
  description,
  aside,
  actions,
  onBack,
  children,
}: {
  trail: ReactNode;
  title: ReactNode;
  id?: string | undefined;
  description?: ReactNode;
  aside?: ReactNode;
  actions?: ReactNode;
  onBack?: (() => void) | undefined;
  children?: ReactNode;
}) {
  const help = usePageHelp();
  const heading = (
    <h1 id={id} className="text-3xl font-bold tracking-[-.04em]">
      {title}
    </h1>
  );
  return (
    <div data-slot="page-header" className="mb-6 flex items-start gap-3">
      <div className="flex min-w-0 flex-1 flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <p className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-content-faint">
            {onBack ? (
              <button
                type="button"
                onClick={onBack}
                className="inline-flex cursor-pointer items-center gap-1 font-semibold text-content-muted transition hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
              >
                <ArrowLeft className="size-3.5" />
                Voltar
              </button>
            ) : null}
            <span>{trail}</span>
          </p>
          {aside ? (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              {heading}
              {aside}
            </div>
          ) : (
            heading
          )}
          {description ? (
            <p className="mt-2 max-w-3xl text-sm leading-6 text-content-muted">{description}</p>
          ) : null}
          {children ? <div className="mt-3">{children}</div> : null}
        </div>
        {actions ? <div className="flex shrink-0 flex-wrap gap-2">{actions}</div> : null}
      </div>
      {help}
    </div>
  );
}

/// O botão de criar a coisa da página ("Nova despesa", "Cadastrar cliente"). É o único botão cheio
/// do cabeçalho e o ícone é sempre o "+": com o ícone escolhido por tela, cada criar tinha um
/// desenho (recibo, etiqueta, pessoa, pacote). Ações secundárias usam `Button variant="outline"`.
export function CreateButton({
  children,
  ...props
}: Omit<ComponentProps<typeof Button>, 'variant' | 'asChild'>) {
  return (
    <Button {...props}>
      <Plus className="size-4" aria-hidden="true" />
      {children}
    </Button>
  );
}
